import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'

import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { REDUCERS } from 'common/store/reducerConstants'

import { siteHeaders } from 'features/Schedule/api/emrHeaders'
import { MAX_SEARCH, SERIES_LIST_PER_PAGE } from 'features/Series/series.constants'

import {
  SERIES_ERROR_CODES,
  toSeriesApiError,
  transformPreview,
  transformRun,
  transformSaveResult,
  transformSeriesList,
  transformTemplate,
  transformTemplates,
} from './series.transform'
import type {
  SeriesCreateBody,
  SeriesListFilter,
  SeriesListPage,
  SeriesPreviewBody,
  SeriesPreviewVisit,
  SeriesRun,
  SeriesSaveWarning,
  SeriesTemplate,
  SeriesTemplateWriteBody,
  SeriesUpdateBody,
} from './series.types'

export const SERIES_TAGS = {
  RUN: 'SeriesRun',
  TEMPLATES: 'SeriesTemplates',
  SERIES_LIST: 'SeriesList',
} as const

type SiteArgs = { siteId: string }
type TemplateArgs = SiteArgs & { templateId: number }
type SaveResult = { series: SeriesRun | null; warnings: SeriesSaveWarning[]; locked: number[] }
// No Series write is ever retried, and a failed load answers now.
const NO_RETRY = { maxRetries: 0 }

const withSite = (siteId: string) => ({ headers: siteHeaders(siteId), keepErrorBody: true })

// Blank filters are left out; the server reads an empty value as its default.
const listParams = ({ status, doctorId, search, page }: SeriesListFilter) => ({
  status,
  ...(doctorId === null ? {} : { doctor_id: doctorId }),
  ...(search.trim() ? { search: search.trim().slice(0, MAX_SEARCH) } : {}),
  page,
  per_page: SERIES_LIST_PER_PAGE,
})

// A 502 may still have saved and a 404 means the list is stale, so templates reload; a refusal changed nothing.
const templateWriteTags = (result: SeriesTemplate | undefined, error: unknown) => {
  if (result) return [SERIES_TAGS.TEMPLATES]
  const failure = toSeriesApiError(error)
  return failure.code === SERIES_ERROR_CODES.EMR_UNAVAILABLE || failure.status === 404
    ? [SERIES_TAGS.TEMPLATES]
    : []
}

export const seriesApi = createApi({
  reducerPath: REDUCERS.SERIES,
  baseQuery: baseQuery(),
  tagTypes: Object.values(SERIES_TAGS),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    fetchSeriesTemplates: build.query<SeriesTemplate[], SiteArgs & { includeRetired: boolean }>({
      query: ({ siteId, includeRetired }) => ({
        url: '/series/templates',
        params: { include_retired: includeRetired ? 'true' : 'false' },
        ...withSite(siteId),
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformTemplates,
      providesTags: [SERIES_TAGS.TEMPLATES],
    }),

    previewSeries: build.mutation<SeriesPreviewVisit[], SiteArgs & { data: SeriesPreviewBody }>({
      query: ({ siteId, data }) => ({
        url: '/series/preview',
        method: HTTP_METHODS.POST,
        data,
        ...withSite(siteId),
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformPreview,
    }),

    createSeries: build.mutation<SaveResult, SiteArgs & { data: SeriesCreateBody }>({
      query: ({ siteId, data }) => ({
        url: '/series',
        method: HTTP_METHODS.POST,
        data,
        ...withSite(siteId),
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformSaveResult,
      invalidatesTags: [SERIES_TAGS.SERIES_LIST, SERIES_TAGS.TEMPLATES],
    }),

    fetchSeriesRun: build.query<SeriesRun | null, SiteArgs & { id: number }>({
      query: ({ siteId, id }) => ({ url: `/series/${id}`, ...withSite(siteId) }),
      extraOptions: NO_RETRY,
      transformResponse: transformRun,
      providesTags: (_result, _error, { id }) => [{ type: SERIES_TAGS.RUN, id }],
    }),

    updateSeries: build.mutation<SaveResult, SiteArgs & { id: number; data: SeriesUpdateBody }>({
      query: ({ siteId, id, data }) => ({
        url: `/series/${id}`,
        method: HTTP_METHODS.PUT,
        data,
        ...withSite(siteId),
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformSaveResult,
      invalidatesTags: [SERIES_TAGS.SERIES_LIST],
      // The answer is the saved run, so its cache takes it as is.
      async onQueryStarted({ siteId, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data.series) {
            dispatch(seriesApi.util.upsertQueryData('fetchSeriesRun', { siteId, id }, data.series))
          }
        } catch {
          // The save hook reports the failure.
        }
      },
    }),

    fetchSeriesList: build.query<SeriesListPage, SiteArgs & { filter: SeriesListFilter }>({
      query: ({ siteId, filter }) => ({
        url: '/series',
        params: listParams(filter),
        ...withSite(siteId),
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformSeriesList,
      providesTags: [SERIES_TAGS.SERIES_LIST],
    }),

    createSeriesTemplate: build.mutation<
      SeriesTemplate,
      SiteArgs & { data: SeriesTemplateWriteBody }
    >({
      query: ({ siteId, data }) => ({
        url: '/series/templates',
        method: HTTP_METHODS.POST,
        data,
        ...withSite(siteId),
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformTemplate,
      invalidatesTags: templateWriteTags,
    }),

    updateSeriesTemplate: build.mutation<
      SeriesTemplate,
      TemplateArgs & { data: SeriesTemplateWriteBody }
    >({
      query: ({ siteId, templateId, data }) => ({
        url: `/series/templates/${templateId}`,
        method: HTTP_METHODS.PUT,
        data,
        ...withSite(siteId),
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformTemplate,
      invalidatesTags: templateWriteTags,
    }),

    duplicateSeriesTemplate: build.mutation<SeriesTemplate, TemplateArgs & { name: string }>({
      query: ({ siteId, templateId, name }) => ({
        url: `/series/templates/${templateId}/duplicate`,
        method: HTTP_METHODS.POST,
        data: { name },
        ...withSite(siteId),
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformTemplate,
      invalidatesTags: templateWriteTags,
    }),

    setSeriesTemplateActive: build.mutation<SeriesTemplate, TemplateArgs & { active: boolean }>({
      query: ({ siteId, templateId, active }) => ({
        url: `/series/templates/${templateId}/active`,
        method: HTTP_METHODS.PATCH,
        data: { active },
        ...withSite(siteId),
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformTemplate,
      invalidatesTags: templateWriteTags,
    }),
  }),
})

export const {
  useFetchSeriesTemplatesQuery,
  usePreviewSeriesMutation,
  useCreateSeriesMutation,
  useFetchSeriesRunQuery,
  useLazyFetchSeriesRunQuery,
  useUpdateSeriesMutation,
  useFetchSeriesListQuery,
  useCreateSeriesTemplateMutation,
  useUpdateSeriesTemplateMutation,
  useDuplicateSeriesTemplateMutation,
  useSetSeriesTemplateActiveMutation,
} = seriesApi
